import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsMubiLeasingConfig } from 'app/store/data/formsConfig';
import { selectShouldCheckIfConsentResetRequired } from 'app/store/data/consents';
import { getQuotationId } from 'app/store/data/quotation';
import { checkAndResetMarketingConsentIfRequired } from 'app/shared/helpers/checkAndResetMarketingConsent';
import { isEmailValid } from 'app/validators/validateEmail';
import { EMailControlProps } from 'app/components/Controls/EMailControl/EMailControl.interfaces';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { validateEmailDomainTypo } from 'app/validators/validateEmailDomainTypo';
import { setMessageVisibility } from 'app/services/businessFunctionHelpers';
import { isAutofillChange } from 'app/shared/helpers/isAutofillChange';
import { getEmailTypoMessageKey } from 'app/shared/helpers/emailTypoMessage';

const hasWhiteSpace = (value: string): boolean => {
  return /\s/g.test(value);
};

export function useEmailControl({
  input,
  meta: { touched, valid },
  onClick,
  onControlBlur,
  onValueChange,
  setIsValueTooLong,
  formName,
  questionKey,
  customLabelSpacing,
}: EMailControlProps) {
  const dispatch = useDispatch();
  const isMubiLeasingConfig = useSelector(selectIsMubiLeasingConfig);
  const shouldCheckIfConsentResetRequired = useSelector(
    selectShouldCheckIfConsentResetRequired
  );
  const quotationId = useSelector(getQuotationId);
  const labelDense = !(
    formName === FormsEnum.TerminationGeneratorForm ||
    isMubiLeasingConfig ||
    customLabelSpacing
  );

  const [focused, setFocused] = useState(false);
  const error = touched && !valid;

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (hasWhiteSpace(value)) {
      return;
    } else if (value.length > 70) {
      setIsValueTooLong && setIsValueTooLong(true);
      return;
    } else {
      setIsValueTooLong && setIsValueTooLong(false);
    }

    input.onChange(value);

    if (onValueChange) {
      onValueChange(value, false, false);
    }

    if (isAutofillChange(event.target)) {
      handleBlur(value, true);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = async (value: string, isAutofilled?: boolean) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== value) {
      input.onChange(trimmedValue);
      if (onValueChange) {
        onValueChange(trimmedValue, false, false);
      }
    }

    setFocused(false);

    input.onBlur(value);

    const isValid = isEmailValid(value, true);

    if (isValid && shouldCheckIfConsentResetRequired.email) {
      await checkAndResetMarketingConsentIfRequired({
        quotationId,
        dispatch,
        shouldCheck: shouldCheckIfConsentResetRequired.email,
        requestData: {
          email: value,
        },
        fieldToResetPath: {
          type: 'root',
          field: 'email',
        },
        formName,
        consentFieldKey:
          formName === FormsEnum.CheckoutForm
            ? FieldsEnum.InsuranceMarketingConsent
            : FieldsEnum.InsuranceMarketingEmailConsent,
      });
    }

    const shouldPatch = isValid || !value;

    let skipScrollOnBlur;

    if (formName && value && isEmailValid(value, true)) {
      skipScrollOnBlur = validateEmailDomainTypo({
        email: value,
        formName,
        questionKey,
      });
    } else {
      formName &&
        setMessageVisibility({
          formName,
          key: getEmailTypoMessageKey(questionKey),
          visible: false,
        });
    }

    if (onControlBlur) {
      onControlBlur(
        shouldPatch,
        true,
        false,
        skipScrollOnBlur || isAutofilled,
        isAutofilled ? trimmedValue : undefined
      );
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (input.value) return;

    const data = event.clipboardData?.getData?.('text');
    if (!data) return;

    event.preventDefault();

    const trimmed = data.trim();

    if (hasWhiteSpace(trimmed)) {
      return;
    } else if (trimmed.length > 70) {
      setIsValueTooLong?.(true);
      return;
    } else {
      setIsValueTooLong?.(false);
    }

    input.onChange(trimmed);
    onValueChange?.(trimmed, false, false);
  };

  return {
    error,
    focused,
    handleBlur,
    handleChange,
    handleClick,
    onFocus,
    labelDense,
    handlePaste,
  };
}
